import React, { useState, type FormEvent } from 'react';
import { NumberInput, RadioButtonGroupInput } from 'react-admin';
import {
  getClassificationFromSource,
  getSourceAndLabel,
  updateProRateCents,
  updateChargeRateCents,
} from '../PositionRateSettingsForm.utils';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { useFormContext } from 'react-hook-form';

type Props = {
  source: string;
  inheritedSettings: any;
};

const choices = [
  { id: 'lock_charge_rate', name: 'Lock Charge Rate' },
  { id: 'lock_professional_rate', name: 'Lock Professional Rate' },
];

export const MarginPercentInput = ({ source, inheritedSettings }: Props) => {
  const [isLockOptionVisible, setIsLockOptionVisible] = useState(false);
  const [lockedRateType, setLockedRateType] = useState<
    'lock_professional_rate' | 'lock_charge_rate' | null
  >(null);

  const { source: inputSource, label, field } = getSourceAndLabel(source);
  const { watch, setValue } = useFormContext();
  const { tab } = getClassificationFromSource(source);
  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings: inheritedSettings });
  const inheritedSettingsHash = rateSettingsDisplayHelper.getSettingsForClassification(tab);

  const professionalRate = watch(`${source}.pro_rate_cents`);
  const chargeRate =
    watch(`${source}.charge_rate_cents`) || inheritedSettingsHash?.charge_rate_cents;
  const markup = watch(`${source}.blended_rates.blended_rate_markup`);
  const blendedRate = watch(`${source}.blended_rate_cents`) || chargeRate * (1 + (markup || 0));
  const useBlendedRate = watch(`${source}.use_blended_rate`);

  let applicableField = field;

  if (source.includes('w2.local')) {
    applicableField = 'base_margin_percent_w2';
  }

  const baseMarginPercent = watch(`${source}.${applicableField}`);
  const lockRateValue = watch(`${source}.lockedRateType`) || 'lock_charge_rate';

  let applicableRate = chargeRate;
  if (useBlendedRate) {
    applicableRate = blendedRate;
  }

  const isMarginValid = (margin: number | string): boolean => {
    const formattedMargin = typeof margin === 'string' ? Number(margin) : margin;

    return !isNaN(formattedMargin) && formattedMargin >= 0 && formattedMargin < 1;
  };

  function handleLockOptionChange(event: FormEvent<HTMLDivElement>) {
    const value = (event.target as HTMLInputElement).value as
      | 'lock_professional_rate'
      | 'lock_charge_rate';
    setLockedRateType(value);
    setValue(`${source}.lockedRateType`, value);

    if (!isMarginValid(value)) {
      return;
    }

    if (value === 'lock_professional_rate') {
      if (professionalRate && baseMarginPercent) {
        updateChargeRateCents({
          setValue,
          watch,
          source,
          professionalRateCents: professionalRate,
          baseMarginPercent,
          blendedMarkup: markup,
          inheritedSettings: inheritedSettingsHash,
        });
      }
    }

    if (value === 'lock_charge_rate') {
      if (chargeRate && baseMarginPercent) {
        updateProRateCents({
          setValue,
          watch,
          source,
          baseMarginPercent,
          rateCents: applicableRate || inheritedSettingsHash.charge_rate_cents,
          inheritedSettings: inheritedSettingsHash,
        });
      }
    }
  }

  const handleMarginChange = (newMarginValue: number) => {
    if (!isMarginValid(newMarginValue)) {
      return;
    }

    if (lockedRateType === 'lock_professional_rate' && professionalRate) {
      updateChargeRateCents({
        setValue,
        watch,
        source,
        professionalRateCents: professionalRate,
        baseMarginPercent: newMarginValue,
        blendedMarkup: markup,
        inheritedSettings: inheritedSettingsHash,
      });

      return;
    }

    updateProRateCents({
      setValue,
      watch,
      source,
      baseMarginPercent: newMarginValue,
      rateCents: applicableRate || inheritedSettingsHash.charge_rate_cents,
      inheritedSettings: inheritedSettingsHash,
    });
  };

  return (
    <>
      <NumberInput
        source={inputSource}
        label={label}
        helperText={String(inheritedSettingsHash?.[field] || '')}
        onFocus={() => setIsLockOptionVisible(true)}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue)) {
            handleMarginChange(newValue);
          }
        }}
        disabled={field === 'total_margin_percent_w2'}
      />
      {isLockOptionVisible && (
        <RadioButtonGroupInput
          source={`${source}.lockedRateType`}
          choices={choices}
          value={lockRateValue}
          defaultValue="lock_charge_rate"
          label="Calculation Base"
          helperText="Select which rate will be fixed. The other will be automatically calculated based on the entered margin."
          onChange={handleLockOptionChange}
        />
      )}
    </>
  );
};
